'use client'

import { useState } from 'react'

import { Repeat } from 'lucide-react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'

import { getDaysOfWeek } from '../../utils/shared'

interface RepeatButtonProps {
	value: string[]
	onChange: (value: string[]) => void
}

export const RepeatButton = ({ value, onChange }: RepeatButtonProps) => {
	const { t } = useTranslation()
	const [popoverOpen, setPopoverOpen] = useState(false)

	const daysOfWeek = getDaysOfWeek(t)

	const handleCheckedChange = (
		dayValue: string,
		checked: boolean | 'indeterminate'
	) => {
		const currentValue = value || []
		let newValue: string[] = []

		if (dayValue === 'everyday') {
			// If selecting "Every day", clear all specific days
			if (checked) {
				newValue = ['everyday']
			}
			// If deselecting "Every day", keep it empty
		} else {
			// If selecting a specific day, remove "everyday" if present
			if (checked) {
				newValue = currentValue.filter((v) => v !== 'everyday')
				newValue = [...newValue, dayValue]
			} else {
				newValue = currentValue.filter((v) => v !== dayValue)
			}
		}

		onChange(newValue)
	}

	return (
		<Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className="gap-2 h-8 w-full max-w-[120px]"
					type="button"
				>
					<Repeat className="h-3 w-3" />
					{t('entities.schedule.repeat.repeat')}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-64 p-3" align="start">
				<div className="space-y-3">
					<p className="text-xs font-medium">
						{t('entities.schedule.repeat.repeat_every_week_on')}
					</p>
					<div className="space-y-2">
						{daysOfWeek.map((day) => (
							<div key={day.value} className="flex items-center space-x-2">
								<Checkbox
									id={`repeat-${day.value}`}
									checked={value?.includes(day.value)}
									onCheckedChange={(checked) =>
										handleCheckedChange(day.value, checked)
									}
								/>
								<label
									htmlFor={`repeat-${day.value}`}
									className="text-sm font-normal cursor-pointer"
								>
									{day.label}
								</label>
							</div>
						))}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	)
}
